/**
 * Badge / accent colors aligned with the collapsed rail icons in
 * `components/layout/Sidebar.js` (same Bootstrap utility colors).
 *
 * Bootstrap 5 defaults (see `bootstrap/dist/css/bootstrap.min.css` :root).
 */
export const SIDEBAR_MODULE_BADGE = {
  /** Admin — `text-success` */
  admin: { backgroundColor: "#198754", color: "#ffffff" },
  /** Study & User Management — `text-purple` / `$text-purple` in Sidebar.scss */
  study: { backgroundColor: "#6f42c1", color: "#ffffff" },
  /** eISF — `text-primary` */
  eisf: { backgroundColor: "#0d6efd", color: "#ffffff" },
  /** ePSF — `text-epsf` magenta (Sidebar.scss) */
  epsf: { backgroundColor: "#c026d3", color: "#ffffff" },
  /** CAPA — `text-warning` */
  capa: { backgroundColor: "#ffc107", color: "#212529" },
  /** e-Source — #f4364f */
  esource: { backgroundColor: "#f4364f", color: "#ffffff" },
  /** E-Planner — `text-success` */
  eplanner: { backgroundColor: "#198754", color: "#ffffff" },
  /** Participant Manager — `text-pm` orange */
  participantManager: { backgroundColor: "#fd7e14", color: "#ffffff" },
  /** QMS — `text-qms` / `$qms-brand` */
  qms: { backgroundColor: "#243f5a", color: "#ffffff" },
  /** Notifications — `text-info` cyan */
  notifications: { backgroundColor: "#0dcaf0", color: "#052c65" },
};

const SIDEBAR_KEYS_ORDER = [
  "admin",
  "study",
  "eisf",
  "epsf",
  "capa",
  "esource",
  "eplanner",
  "participantManager",
  "qms",
  "notifications",
];

/**
 * Map API `Module` string to a sidebar rail bucket.
 * @param {string} moduleName
 * @returns {keyof typeof SIDEBAR_MODULE_BADGE | null}
 */
export function resolveSidebarModuleKey(moduleName) {
  const raw = String(moduleName || "").trim().toLowerCase();
  const s = raw.replace(/[\s-]+/g, "_");
  if (!s || s === "general") return "notifications";
  if (s.includes("capa")) return "capa";
  if (
    s.includes("esource") ||
    s.includes("e_source") ||
    s.includes("e-source") ||
    (s.includes("source") && !s.includes("resource"))
  ) {
    return "esource";
  }
  if (
    s.includes("eplanner") ||
    s.includes("e_planner") ||
    s.includes("appointment")
  ) {
    return "eplanner";
  }
  if (
    s.includes("participant") ||
    s.includes("erecruitment") ||
    s.includes("e_recruitment") ||
    s.includes("feasibility") ||
    s.includes("campaign")
  ) {
    return "participantManager";
  }
  if (s.includes("qms") || s.includes("dms") || s.includes("document_library")) return "qms";
  /* ePSF before eISF — "epsf" must not fall through to the ISF bucket. */
  if (
    s === "epsf" ||
    s === "e_psf" ||
    s.includes("epsf") ||
    s.includes("e_psf") ||
    s.includes("pharmacy_site_file") ||
    s.includes("pharmacy site file")
  ) {
    return "epsf";
  }
  if (s.includes("isf")) return "eisf";
  if (s.includes("study") || s.includes("stema")) return "study";
  if (
    s.includes("admin") ||
    s.includes("superadmin") ||
    s.includes("organization") ||
    s.includes("usermanagement") ||
    s.includes("role_management")
  ) {
    return "admin";
  }
  if (s.includes("notification")) return "notifications";
  return null;
}

/**
 * Pill/badge style for a module name (same hues as sidebar menu icons).
 * @param {string} moduleName
 * @returns {{ backgroundColor: string, color: string }}
 */
export function getSidebarModuleBadgeStyle(moduleName) {
  const key = resolveSidebarModuleKey(moduleName);
  if (key && SIDEBAR_MODULE_BADGE[key]) return SIDEBAR_MODULE_BADGE[key];
  let h = 0;
  const str = String(moduleName || "x");
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  const pick = SIDEBAR_KEYS_ORDER[Math.abs(h) % SIDEBAR_KEYS_ORDER.length];
  return SIDEBAR_MODULE_BADGE[pick];
}

/** Solid dot color for filter chips — uses the same bucket as badges. */
export function getSidebarModuleDotColor(moduleName) {
  return getSidebarModuleBadgeStyle(moduleName).backgroundColor;
}
